import { Node as PmNode } from 'prosemirror-model'
import { Decoration, DecorationSet } from 'prosemirror-view'

import {
  PageMarginContext,
  buildPageFooterElement,
  buildPageHeaderElement,
} from './pageElements'

export interface PrintPageInfo {
  start: number
  pageNumber: number
  pageAlias?: string
  isTitlePage?: boolean
  pageColor?: string
  collatedRevision?: string
  pageSection?: string
  sceneElementNumber?: string
  dialogueMoreText?: string
  dialogueContdText?: string
  sceneContdHeaderText?: string
  sceneContdFooterText?: string
}

/** Builds header/footer margin-widget decorations for every given print page. */
export function buildPrintPaginationDecorations(
  doc: PmNode,
  pages: readonly PrintPageInfo[],
  marginCtx: PageMarginContext,
): DecorationSet {
  const decorations: Decoration[] = []
  const { page: pageType, 'title-page': titlePageType } = doc.type.schema.nodes

  for (const page of pages) {
    const $pos = doc.resolve(page.start + 1)

    const headerWidget = buildPageHeaderElement(
      page.start,
      {
        pageNumber: page.pageNumber,
        pageAlias: page.pageAlias,
        isTitlePage: page.isTitlePage,
        collatedRevision: page.collatedRevision,
        pageSection: page.pageSection,
        sceneElementNumber: page.sceneElementNumber,
        sceneContdHeaderText: page.sceneContdHeaderText,
        dialogueContdText: page.dialogueContdText,
      },
      marginCtx,
    )
    decorations.push(headerWidget)

    // Tag the page's first block directly so its margin-top can be zeroed
    // in CSS without depending on DOM sibling order.
    const firstBlock = doc.nodeAt(page.start)
    if (firstBlock) {
      decorations.push(
        Decoration.node(page.start, page.start + firstBlock.nodeSize, {
          class: 'print-page-first-block',
        }),
      )
    }

    let pageDepth = $pos.depth
    while (pageDepth > 0) {
      const { type } = $pos.node(pageDepth)
      if (type === pageType || type === titlePageType) break
      pageDepth--
    }

    if (pageDepth > 0) {
      const pageNode = $pos.node(pageDepth)
      const pageNodeStart = $pos.before(pageDepth)
      const pageAttrs: Record<string, string> = {}

      if (page.pageColor) {
        pageAttrs.class = 'has-revision-page-color'
        pageAttrs.style = `--page-revision-color: ${page.pageColor}`
      }

      if (Object.keys(pageAttrs).length > 0) {
        decorations.push(
          Decoration.node(
            pageNodeStart,
            pageNodeStart + pageNode.nodeSize,
            pageAttrs,
          ),
        )
      }

      const footerWidget = buildPageFooterElement(
        pageNodeStart + pageNode.nodeSize - 1,
        {
          pageNumber: page.pageNumber,
          pageAlias: page.pageAlias,
          isTitlePage: page.isTitlePage,
          collatedRevision: page.collatedRevision,
          pageSection: page.pageSection,
          sceneContdFooterText: page.sceneContdFooterText,
          dialogueMoreText: page.dialogueMoreText,
        },
        marginCtx,
      )
      decorations.push(footerWidget)
    }
  }
  return DecorationSet.create(doc, decorations)
}
